// Firebase Auth singleton + typed helpers.
// Ports vanilla auth calls from public/app/index.html (onAuthStateChanged L462,
// signInWithEmailAndPassword L18954, sendEmailVerification L18930, signOut L19259).

import {
  getAuth,
  onAuthStateChanged as _onAuthStateChanged,
  signInWithEmailAndPassword as _signIn,
  createUserWithEmailAndPassword as _signUp,
  sendEmailVerification as _sendVerify,
  sendPasswordResetEmail as _sendReset,
  signOut as _signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  type User,
  type Unsubscribe,
} from 'firebase/auth'
import { firebaseApp } from './app'

export const auth = getAuth(firebaseApp)

// ── Auth state ──────────────────────────────────────────────────────────────
export function onAuthStateChanged(cb: (user: User | null) => void): Unsubscribe {
  return _onAuthStateChanged(auth, cb)
}

export function currentUser(): User | null {
  return auth.currentUser
}

// ── Email/password ──────────────────────────────────────────────────────────
export async function signIn(email: string, password: string): Promise<User> {
  const cred = await _signIn(auth, email, password)
  return cred.user
}

export async function signUp(email: string, password: string): Promise<User> {
  const cred = await _signUp(auth, email, password)
  return cred.user
}

// ── Email verification ──────────────────────────────────────────────────────
export async function sendEmailVerification(): Promise<void> {
  const u = auth.currentUser
  if (!u) throw new Error('sendEmailVerification: no current user')
  await _sendVerify(u)
}

export async function reloadUser(): Promise<User | null> {
  const u = auth.currentUser
  if (!u) return null
  await u.reload()
  return auth.currentUser
}

// ── Password reset ──────────────────────────────────────────────────────────
export async function sendPasswordReset(email: string): Promise<void> {
  await _sendReset(auth, email)
}

// ── Sign out ────────────────────────────────────────────────────────────────
export async function signOut(): Promise<void> {
  await _signOut(auth)
}

// ── Password re-auth ────────────────────────────────────────────────────────
// V1 parity — public/app/index.html:6510 reauthWithPassword. Used to gate
// privilege escalations (grant Partner, promote Admin, remove member, wipe
// data) so a hijacked session can't silently change access. Throws on
// wrong/too-many-retries — callers should surface the error via toast.
export async function reauthCurrentUser(password: string): Promise<void> {
  const u = auth.currentUser
  if (!u || !u.email) throw new Error('Not signed in')
  const cred = EmailAuthProvider.credential(u.email, password)
  await reauthenticateWithCredential(u, cred)
}
